import { z } from 'zod';
import { E2EFlowPathSchema, FlowStepType } from './e2eFlowTemplate.js';
// ============= Enhancement Depth Levels (Issue #392, #426) =============
/**
 * Depth level for AI enhancement analysis
 * - 'standard': Uses AST-generated context (flow info, steps, API calls, DB queries, evidence snippets)
 * - 'deep': Standard + full source file contents from all evidence file paths
 * - 'ultra-deep': Deep + call graph, function signatures, TypeScript types (Issue #426 Phase 3)
 */
export const EnhancementDepth = z.enum(['standard', 'deep', 'ultra-deep']);
// ============= Ultra-Deep Mode Context (Issue #426 Phase 3) =============
/**
 * Function signature for call graph context
 */
export const FunctionSignatureSchema = z.object({
    /** Function name */
    name: z.string(),
    /** File path where function is defined */
    filePath: z.string(),
    /** Line number of definition */
    line: z.number().optional(),
    /** Function parameters with types */
    params: z.array(z.object({
        name: z.string(),
        type: z.string().optional(),
    })),
    /** Return type */
    returnType: z.string().optional(),
    /** Whether it's async */
    isAsync: z.boolean().optional(),
    /** Brief description from JSDoc/comments */
    description: z.string().optional(),
});
/**
 * Call graph entry showing function dependencies
 */
export const CallGraphEntrySchema = z.object({
    /** The calling function */
    caller: z.string(),
    /** Functions called by this function */
    callees: z.array(z.string()),
    /** File path */
    filePath: z.string(),
});
/**
 * TypeScript type definition
 */
export const TypeDefinitionSchema = z.object({
    /** Type name */
    name: z.string(),
    /** Type kind (interface, type alias, enum, etc.) */
    kind: z.enum(['interface', 'type', 'enum', 'class']),
    /** File path where defined */
    filePath: z.string(),
    /** Type definition as string (simplified) */
    definition: z.string(),
    /** Related types (extends, uses) */
    relatedTypes: z.array(z.string()).optional(),
});
/**
 * Data transformation between steps
 */
export const DataTransformationSchema = z.object({
    /** Source step ID */
    fromStepId: z.string(),
    /** Target step ID */
    toStepId: z.string(),
    /** Input data shape/type */
    inputType: z.string().optional(),
    /** Output data shape/type */
    outputType: z.string().optional(),
    /** Description of transformation */
    transformation: z.string(),
});
/**
 * Ultra-deep context for comprehensive AI analysis (Issue #426 Phase 3)
 */
export const UltraDeepContextSchema = z.object({
    /** Call graph starting from handler function */
    callGraph: z.array(CallGraphEntrySchema),
    /** Function signatures for key functions */
    functionSignatures: z.array(FunctionSignatureSchema),
    /** Relevant TypeScript type definitions */
    typeDefinitions: z.array(TypeDefinitionSchema),
    /** Data transformations between steps */
    dataTransformations: z.array(DataTransformationSchema),
    /** Entry point function name */
    entryPoint: z.string(),
    /** Total functions analyzed */
    functionsAnalyzed: z.number(),
    /** Total types extracted */
    typesExtracted: z.number(),
});
// ============= Enhancement Types =============
/**
 * Type of enhancement suggested by AI
 */
export const EnhancementType = z.enum([
    'name', // Flow name improvement
    'description', // Flow description improvement
    'stepLabel', // Individual step label improvement
    'stepDescription', // Individual step description improvement
    'missingStep', // Detected missing step in flow
    'completeness', // Overall completeness assessment
]);
/**
 * Evidence level for AI suggestions (Issue #407)
 * - 'strong': Based on actual code analysis (file contents, evidence snippets)
 * - 'partial': Based on detected patterns (AST, API calls, DB queries)
 * - 'inferred': No direct evidence, AI reasoning only
 */
export const EvidenceLevel = z.enum(['strong', 'partial', 'inferred']);
/**
 * Category for missing step suggestions (Issue #436)
 * - 'missed': Step exists in code but was not detected by the analysis process
 *   (has concrete code evidence with file paths, line numbers, code snippets)
 * - 'suggested': AI recommendation for a step that doesn't exist in code
 *   (best-practice or pattern-based suggestion)
 */
export const MissingStepCategory = z.enum(['missed', 'suggested']);
/**
 * Code reference for missed steps (Issue #436)
 * Provides specific code details that prove the step exists
 */
export const CodeReferenceSchema = z.object({
    /** Function name where the code exists */
    functionName: z.string().optional(),
    /** Variable or state name involved */
    variableName: z.string().optional(),
    /** API endpoint related to this step */
    apiEndpoint: z.string().optional(),
    /** Specific code snippet that proves this step exists */
    codeSnippet: z.string().optional(),
});
/**
 * Evidence source for an enhancement
 */
export const EvidenceSourceSchema = z.object({
    /** Type of evidence */
    type: z.enum(['file', 'snippet', 'pattern', 'ast']),
    /** File path (if applicable) */
    filePath: z.string().optional(),
    /** Line numbers (if applicable) */
    lineStart: z.number().optional(),
    lineEnd: z.number().optional(),
    /** Description of evidence */
    description: z.string(),
});
/**
 * Individual enhancement suggestion from AI
 */
export const FlowEnhancementSchema = z.object({
    /** Type of enhancement */
    type: EnhancementType,
    /** Step ID (for step-specific enhancements) */
    stepId: z.string().optional(),
    /** Original value being enhanced */
    original: z.string().optional(),
    /** AI-suggested improvement */
    suggested: z.string(),
    /** AI reasoning for this suggestion */
    reasoning: z.string(),
    /** Confidence score (0-1) */
    confidence: z.number().min(0).max(1),
    /** User acceptance status (set during review) */
    accepted: z.boolean().optional(),
    /** Evidence level for this suggestion (Issue #407) */
    evidenceLevel: EvidenceLevel.optional(),
    /** Evidence sources that support this suggestion */
    evidenceSources: z.array(EvidenceSourceSchema).optional(),
});
/**
 * Missing step suggestion from AI
 */
export const MissingStepSuggestionSchema = z.object({
    /** Step ID after which to insert */
    afterStepId: z.string(),
    /** Suggested step type */
    suggestedType: FlowStepType,
    /** Suggested step name/label */
    suggestedName: z.string(),
    /** Description of what this step should do */
    description: z.string(),
    /** Why this step is missing */
    reasoning: z.string(),
    /** Confidence score */
    confidence: z.number().min(0).max(1),
    /** Evidence level for this suggestion (Issue #407) */
    evidenceLevel: EvidenceLevel.optional(),
    /** Evidence sources that support this suggestion */
    evidenceSources: z.array(EvidenceSourceSchema).optional(),
    /**
     * Category: 'missed' vs 'suggested' (Issue #436)
     * - 'missed': Step exists in code but wasn't detected
     * - 'suggested': AI recommendation, code doesn't exist yet
     */
    category: MissingStepCategory.optional(),
    /**
     * Code reference for 'missed' steps (Issue #436)
     * Provides specific code details proving the step exists
     */
    codeReference: CodeReferenceSchema.optional(),
});
/**
 * Completeness assessment from AI
 */
export const CompletenessAssessmentSchema = z.object({
    /** Overall completeness score (0-100) */
    score: z.number().min(0).max(100),
    /** Explanation of the score */
    reasoning: z.string(),
    /** Suggestions for improvement */
    suggestions: z.array(z.string()),
    /** Identified gaps in the flow */
    gaps: z.array(z.string()).optional(),
});
/**
 * Step enhancement from AI
 */
export const StepEnhancementSchema = z.object({
    /** Step ID being enhanced */
    stepId: z.string(),
    /** Current label */
    originalLabel: z.string().optional(),
    /** Suggested label */
    suggestedLabel: z.string(),
    /** Current description */
    originalDescription: z.string().optional(),
    /** Suggested description */
    suggestedDescription: z.string().optional(),
    /** Reasoning for the suggestion */
    reasoning: z.string(),
    /** Confidence score */
    confidence: z.number().min(0).max(1),
    /** Evidence level for this suggestion (Issue #407) */
    evidenceLevel: EvidenceLevel.optional(),
    /** Evidence sources that support this suggestion */
    evidenceSources: z.array(EvidenceSourceSchema).optional(),
});
// ============= Parse Status (Issue #426) =============
/**
 * Parse status for AI response processing
 * - 'success': All enhancements parsed successfully
 * - 'partial': Some enhancements were filtered/fixed (check parseWarnings)
 * - 'failed': Parsing failed completely (fallback result returned)
 */
export const ParseStatus = z.enum(['success', 'partial', 'failed']);
// ============= Enhancement Result =============
/**
 * Complete result from AI enhancement
 */
export const EnhancementResultSchema = z.object({
    /** Flow ID that was enhanced */
    flowId: z.string(),
    /** Depth level used for analysis */
    depth: EnhancementDepth,
    /** Suggested flow name */
    suggestedName: z.string().optional(),
    /** Original flow name */
    originalName: z.string().optional(),
    /** Suggested flow description */
    suggestedDescription: z.string().optional(),
    /** Original flow description */
    originalDescription: z.string().optional(),
    /** Step-specific enhancements */
    stepEnhancements: z.array(StepEnhancementSchema),
    /** Missing steps detected */
    missingSteps: z.array(MissingStepSuggestionSchema),
    /** Completeness assessment */
    completeness: CompletenessAssessmentSchema,
    /** All enhancements as a flat list (for UI iteration) */
    enhancements: z.array(FlowEnhancementSchema),
    /** Tokens used for this enhancement */
    tokensUsed: z.number().optional(),
    /** Time taken (ms) */
    durationMs: z.number().optional(),
    /** Whether using cached result */
    cached: z.boolean().optional(),
    /** Full prompt sent to AI (Issue #424) */
    promptSent: z.string().optional(),
    /** Raw AI response JSON (Issue #424) */
    aiResponse: z.string().optional(),
    /** Parse status for AI response (Issue #426) */
    parseStatus: ParseStatus.optional(),
    /** Warnings from parsing (e.g., filtered/fixed items) (Issue #426) */
    parseWarnings: z.array(z.string()).optional(),
    /** Count of enhancements dropped during validation (Issue #426) */
    droppedEnhancements: z.number().optional(),
});
// ============= Enhancement Request =============
/**
 * Request to enhance an E2E flow with AI
 */
export const EnhanceFlowRequestSchema = z.object({
    /** Depth level for analysis */
    depth: EnhancementDepth.default('standard'),
    /** Custom guidelines for AI (optional) */
    customGuidelines: z.string().max(2000).optional(),
    /** Whether to include file contents even for standard depth (override) */
    forceIncludeFiles: z.boolean().optional(),
});
/**
 * Request to apply accepted enhancements
 */
export const ApplyEnhancementsRequestSchema = z.object({
    /** Enhancements to apply (only accepted ones) */
    enhancements: z.array(FlowEnhancementSchema),
    /** Step enhancements to apply */
    stepEnhancements: z.array(StepEnhancementSchema).optional(),
    /** Missing steps to add */
    missingSteps: z.array(MissingStepSuggestionSchema).optional(),
    /** Updated flow name */
    flowName: z.string().optional(),
    /** Updated flow description */
    flowDescription: z.string().optional(),
});
// ============= Context Types =============
/**
 * AST-derived context for standard depth analysis
 */
export const ASTFlowContextSchema = z.object({
    /** Flow name */
    flowName: z.string(),
    /** Flow description */
    flowDescription: z.string().optional(),
    /** Trigger type */
    triggerType: z.string(),
    /** Trigger name */
    triggerName: z.string(),
    /** Handler function name */
    handlerName: z.string().optional(),
    /** Element text */
    elementText: z.string().optional(),
    /** Interaction category */
    interactionCategory: z.string().optional(),
    /** Is flow complete */
    isComplete: z.boolean(),
    /** Step summaries */
    steps: z.array(z.object({
        id: z.string(),
        type: z.string(),
        name: z.string(),
        label: z.string(),
        description: z.string().optional(),
    })),
    /** API calls in the flow */
    apiCalls: z.array(z.object({
        method: z.string(),
        endpoint: z.string(),
        wrapperFunction: z.string().optional(),
    })),
    /** Database queries in the flow */
    databaseQueries: z.array(z.object({
        operation: z.string(),
        table: z.string(),
        model: z.string().optional(),
    })),
    /** State changes */
    stateChanges: z.array(z.string()),
    /** Touched services */
    touchedServices: z.array(z.string()),
    /** Touched tables */
    touchedTables: z.array(z.string()),
    /** Evidence snippets */
    evidenceSnippets: z.array(z.string()),
});
/**
 * File content for deep analysis
 */
export const FileContentSchema = z.object({
    /** Relative file path */
    filePath: z.string(),
    /** File content (may be truncated) */
    content: z.string(),
    /** Why this file is relevant */
    relevance: z.string(),
    /** Whether content was truncated */
    truncated: z.boolean().optional(),
    /** Original size before truncation */
    originalSize: z.number().optional(),
});
/**
 * Complete enhancement context
 */
export const EnhancementContextSchema = z.object({
    /** The flow being enhanced */
    flow: E2EFlowPathSchema,
    /** Depth level */
    depth: EnhancementDepth,
    /** AST-derived context (always present) */
    astContext: ASTFlowContextSchema,
    /** File contents (only for deep analysis) */
    fileContents: z.array(FileContentSchema).optional(),
    /** Ultra-deep context (only for ultra-deep analysis) (Issue #426 Phase 3) */
    ultraDeepContext: UltraDeepContextSchema.optional(),
    /** Total context size (for token estimation) */
    contextSizeBytes: z.number().optional(),
});
// ============= Enhancement History =============
/**
 * Historical enhancement record
 */
export const EnhancementHistoryRecordSchema = z.object({
    /** Record ID */
    id: z.string(),
    /** Project ID */
    projectId: z.string(),
    /** Flow ID */
    flowId: z.string(),
    /** Depth used */
    depth: EnhancementDepth,
    /** Original flow snapshot */
    originalFlow: E2EFlowPathSchema,
    /** Enhanced flow (if applied) */
    enhancedFlow: E2EFlowPathSchema.optional(),
    /** All enhancements suggested */
    enhancements: z.array(FlowEnhancementSchema),
    /** Completeness score */
    completenessScore: z.number().optional(),
    /** Tokens used */
    tokensUsed: z.number().optional(),
    /** Whether changes were applied */
    applied: z.boolean(),
    /** Full prompt sent to AI (Issue #424) */
    promptSent: z.string().optional(),
    /** Raw AI response JSON (Issue #424) */
    aiResponse: z.string().optional(),
    /** Created timestamp */
    createdAt: z.string().datetime(),
    /** Updated timestamp */
    updatedAt: z.string().datetime(),
});
//# sourceMappingURL=e2eFlowEnhancement.js.map